import * as angular from "angular";
import swal from "sweetalert2";
import { v7 as uuid } from "uuid";

import { FORM_ENTITY_CREATE } from "../../../../../../../routes";
import { isSupportedOrNullish } from "../../../../../isSupportedOrNullish";
import { getPrimaryClassificationsForType } from "../../../../../getAllowedClassifications";
import {
	buildEntitySavePayload,
	mergeSavedEntity
} from "../../../../../entity-save-payload";
import {
	getEntityOverviewSchema,
	mergeEntitySchemaSection
} from "../../../../../entity-schema-state";
import {
	ENTITY_TRANSLATION_KEYS,
	I18NextContext
} from "../../../../../../../services/i18nService";
import {
	getEntityTimeZone,
	hasSystemKeyValues,
	mapEntityForForm
} from "../entityFormModel";
import jsonSchemaForm from "../../../../../../../extensions/json-schema/json-schema-form";
import template from "./entity-form-editor.html";
import "./entity-form-editor.less";

export function controller(appState, toastr, loadingService, entityManager, $logger, uiHelpers, $scope) {
	const ctrl = this;

	ctrl.i18n = I18NextContext.Instance;
	ctrl.ENTITY_TRANSLATION_KEYS = ENTITY_TRANSLATION_KEYS;
	ctrl.$onChanges = onChanges;
	ctrl.beginEditing = beginEditing;
	ctrl.leaveEditing = leaveEditing;
	ctrl.saveChanges = saveChanges;
	ctrl.save = saveChanges;
	ctrl.handleSubmit = handleSubmit;
	ctrl.canEdit = canEdit;
	ctrl.hasEditableExistingFields = hasEditableExistingFields;
	ctrl.isDirty = isDirty;
	ctrl.isSupportedOrNullish = isSupportedOrNullish;

	ctrl.$onInit = () => {
		ctrl.isLoading = false;

		ctrl.entityTypeName = ctrl.entityType.Code.charAt(0).toUpperCase()
			+ ctrl.entityType.Code.slice(1).toLowerCase();
		ctrl.entityTypeDisplayName = ctrl.entityType.Name
			?? ctrl.entityType.Type?.Name
			?? ctrl.entityType.Code;
		ctrl.hasSystemKeyInputs = !ctrl.entityType.IsCodeSystemManaged
			|| ctrl.entityType.Classification?.IsSupported
			|| ctrl.entityType.ExternalKey?.IsSupported;

		ctrl.allClassifications = getPrimaryClassificationsForType(
			ctrl.entityType.Classifications,
			appState.forms.selected.Agency.Classifications
		);

		const defaultName = `${ctrl.entityType.Code} ${ctrl.entityType.Code === "FISHERMAN" && !!ctrl.entityType.Name ? "Info" : "Name"}`;
		ctrl.tooltipTranslations = {
			name: ctrl.i18n.translateEntityType(
				ctrl.entityType.Code,
				ENTITY_TRANSLATION_KEYS.LABELS.Name,
				defaultName
			),
			code: ctrl.i18n.translateEntityType(
				ctrl.entityType.Code,
				ENTITY_TRANSLATION_KEYS.LABELS.Code,
				"Number"
			),
			classification: ctrl.i18n.translateEntityType(
				ctrl.entityType.Code,
				ENTITY_TRANSLATION_KEYS.LABELS.Classification,
				"Classification"
			),
			externalKey: ctrl.i18n.translateEntityType(
				ctrl.entityType.Code,
				ENTITY_TRANSLATION_KEYS.LABELS.ExternalKey,
				"External Key"
			)
		};

		initializeForEntity(ctrl.entity);
	};

	function onChanges(changes) {
		const entityChange = changes.entity;
		if(!entityChange || entityChange.isFirstChange() || entityChange.currentValue === undefined) {
			return;
		}

		if(!isDirty()) {
			initializeForEntity(entityChange.currentValue);
		}
	}

	function initializeForEntity(entity) {
		ctrl.isNewEntity = entity === undefined;
		ctrl.formEntity = entity !== undefined
			? mapEntityForForm(entity, entityManager)
			: createNewEntityDraft();

		ctrl.hasSystemKeyValues = hasSystemKeyValues(ctrl.entityType, ctrl.formEntity);
		initializeSchemas();
		syncEditSnapshot();
		syncDraft();
	}

	function createNewEntityDraft() {
		const defaultTimeZone = appState.forms.selected.TimeMode.Code === "BYENTITY"
			? undefined
			: appState.forms.selected.TimeZone;
		const formEntity = { TimeZone: defaultTimeZone };

		if(ctrl.entityType.IsCodeSystemManaged) {
			formEntity.Code = uuid();
		}

		if(ctrl.entityType.IsExternalKeySystemManaged) {
			formEntity.ExternalKey = uuid();
		}

		return formEntity;
	}

	function beginEditing() {
		if(!canEdit()) {
			return false;
		}

		if(!ctrl.isNewEntity) {
			initializeForEntity(ctrl.entity);
		}

		return true;
	}

	function leaveEditing() {
		if(ctrl.isNewEntity) {
			return false;
		}

		ctrl.formEntity = angular.copy(ctrl.editSnapshot.formEntity);
		ctrl.overviewModel = angular.copy(ctrl.editSnapshot.overviewModel);
		ctrl.overviewControl?.reset(ctrl.overviewModel);
		syncDraft();

		return true;
	}

	function canEdit() {
		return ctrl.isNewEntity
			|| (!ctrl.readonly && hasEditableExistingFields());
	}

	function isDirty() {
		return !angular.equals(ctrl.formEntity, ctrl.editSnapshot?.formEntity)
			|| !angular.equals(ctrl.overviewModel, ctrl.editSnapshot?.overviewModel);
	}

	function syncEditSnapshot() {
		ctrl.editSnapshot = {
			formEntity: angular.copy(ctrl.formEntity),
			overviewModel: angular.copy(ctrl.overviewModel)
		};
	}

	function syncDraft() {
		ctrl.draft = ctrl.formEntity;
	}

	function handleSubmit() {
		const saveAction = ctrl.section.activeActions
			.find(action => action.code === "save");

		return saveAction
			? ctrl.section.invokeAction(saveAction)
			: false;
	}

	function hasEditableExistingFields() {
		const hasEditableNameOrContact = !ctrl.entityType.IsNameDefaultedToCodeWhenNoContact
			|| !!ctrl.entityType.Contact?.IsSupported;
		const hasEditableTimezone = isSupportedOrNullish(ctrl.entityType.TimeZone);
		const hasEditableOverview = !!ctrl.overviewSchema;

		return hasEditableNameOrContact || hasEditableTimezone || hasEditableOverview;
	}

	function initializeSchemas() {
		ctrl.overviewSchema = getEntityOverviewSchema(ctrl.entityType.Schema);
		ctrl.overviewModel = angular.copy(ctrl.formEntity.Overview === undefined ? {} : ctrl.formEntity.Overview);
		ctrl.overviewControl?.reset(ctrl.overviewModel);
	}

	ctrl.setClassificationBox = (box) => {
		ctrl.classificationBox = box;

		const selectionChoices = uiHelpers.setDisplay(
			ctrl.allClassifications,
			{
				recentlyUsedEnabled: false,
				hideCode: true
			}
		);

		if(ctrl.classificationBox) {
			ctrl.classificationBox.setChoices(selectionChoices, "value", "label", true);
		}
	};

	ctrl.setDisplay = (id, fieldVariable) => {
		const item = ctrl[fieldVariable]
			.find(candidate => candidate.Id === parseInt(id) || candidate.Id === id);

		if(!item) {
			$logger.warn(`Selected ${fieldVariable} not found as an option.`);
		}

		return item.Name || item.Code;
	};

	ctrl.handleClassificationValueChanged = (modelVal) => {
		const modelValInt = parseInt(modelVal);
		const selectedClassification = ctrl.allClassifications
			.find(classification => classification.Id === modelValInt);

		ctrl.formEntity.Classification = selectedClassification
			? { Code: selectedClassification.Code }
			: undefined;
	};

	async function saveChanges() {
		if(ctrl.readonly || (!ctrl.isNewEntity && !hasEditableExistingFields())) {
			return false;
		}
		if(!ctrl.isNewEntity && !isDirty()) {
			return true;
		}

		if(!validate()) {
			return false;
		}

		const entityCandidate = buildEntityCandidate();

		if(entityCandidate.Id) {
			ctrl.isLoading = true;
			try {
				const result = await ctrl.onSave({ entity: entityCandidate });
				const savedEntity = mergeSavedEntity(entityCandidate, result);
				ctrl.formEntity = mapEntityForForm(savedEntity, entityManager);
				initializeSchemas();
				syncEditSnapshot();
				syncDraft();

				return true;
			}
			catch(err) {
				$logger.error("entity update error", err);
				throw err;
			}
			finally {
				ctrl.isLoading = false;
			}
		}

		const params = buildEntitySavePayload(entityCandidate, ctrl.entityType);
		const createCandidate = {
			...entityCandidate,
			...params
		};

		return createEntity(params, createCandidate);
	}

	function buildEntityCandidate() {
		const candidate = { ...(ctrl.entity ?? ctrl.formEntity) };
		const editableKeys = ["Code", "Name", "Contact", "TimeZone", "Classification", "ExternalKey"];

		for(const key of editableKeys) {
			if(ctrl.isNewEntity || !angular.equals(ctrl.formEntity[key], ctrl.editSnapshot.formEntity[key])) {
				if(ctrl.formEntity[key] === undefined) {
					delete candidate[key];
				} else {
					candidate[key] = angular.copy(ctrl.formEntity[key]);
				}
			}
		}

		return ctrl.overviewSchema && !angular.equals(ctrl.overviewModel, ctrl.editSnapshot.overviewModel)
			? mergeEntitySchemaSection(candidate, "Overview", ctrl.overviewModel)
			: candidate;
	}

	async function createEntity(entity, entityCandidate) {
		const route = FORM_ENTITY_CREATE(appState.forms.selected.Id);
		const entityPayload = {
			...entity,
			Type: { Code: ctrl.entityType.Code }
		};

		try {
			const confirmed = await showEntityConfirmation(entityCandidate);
			if(!confirmed) {
				return false;
			}

			ctrl.isLoading = true;
			const newEntities = await loadingService.watch(
				ctrl.post(route, [entityPayload])
			);
			const createdEntity = mergeSavedEntity(entityCandidate, newEntities[0]);

			toastr.success(`${createdEntity.Name ?? createdEntity.Code} created successfully.`);
			ctrl.onCreate({ entity: createdEntity });

			return true;
		}
		catch(err) {
			$logger.error("entity creation error", err);
			toastr.error("There was an error creating a new entity");

			return false;
		}
		finally {
			ctrl.isLoading = false;
		}
	}

	async function showEntityConfirmation(entity) {
		const showExternalKey = !ctrl.entityType.IsExternalKeySystemManaged
			&& entity.ExternalKey !== undefined;
		const showCode = !ctrl.entityType.IsCodeSystemManaged;
		const showName = !ctrl.entityType.IsNameDefaultedToCodeWhenNoContact
			|| ctrl.entityType.Contact.IsSupported;

		const readonlyOnCreateValues = [];
		if(showCode) {
			readonlyOnCreateValues.push(ctrl.tooltipTranslations.code);
		}
		if(entity.Classification !== undefined) {
			readonlyOnCreateValues.push(ctrl.tooltipTranslations.classification);
		}
		if(entity.ExternalKey !== undefined && showExternalKey) {
			readonlyOnCreateValues.push(ctrl.tooltipTranslations.externalKey);
		}

		let confirmValText = readonlyOnCreateValues[0] ?? "";
		if(readonlyOnCreateValues.length === 2) {
			confirmValText = `${confirmValText} and ${readonlyOnCreateValues[1]}`;
		}
		else if(readonlyOnCreateValues.length === 3) {
			confirmValText = `${confirmValText}, ${readonlyOnCreateValues[1]}, and ${readonlyOnCreateValues[2]}`;
		}
		const timeZone = getEntityTimeZone(entity.TimeZone?.Code);

		const confirmation = await swal.fire({
			title: "Create Entity Confirmation",
			html:
				`<p>You are about to create a new ${ctrl.entityType.Code.toLowerCase()}.</p>
				<div class='entity-confirm'>
					${entity.Name !== undefined && showName ? `<div class='prop-label'>${ctrl.tooltipTranslations.name}:</div>` : ""}
					${entity.Name !== undefined && showName ? `<div class='value'>${entity.Name}</div>` : ""}
					${showCode ? `<div class='prop-label'>${ctrl.tooltipTranslations.code}: </div>` : ""}
					${showCode ? `<div class='value'>${entity.Code}</div>` : ""}
					${entity.Classification !== undefined ? `<div class='prop-label'>${ctrl.tooltipTranslations.classification}:</div>` : ""}
					${entity.Classification !== undefined ? `<div class='value'>${entity.Classification.Code}</div>` : ""}
					${timeZone !== undefined ? "<div class='prop-label'>Timezone:</div>" : ""}
					${timeZone !== undefined ? `<div class='value'>${timeZone.name}</div>` : ""}
					${showExternalKey ? `<div class='prop-label'>${ctrl.tooltipTranslations.externalKey}:</div>` : ""}
					${showExternalKey ? `<div class='value'>${entity.ExternalKey}</div>` : ""}
					<br/>
					${readonlyOnCreateValues.length > 0 ? `<div class='prop-label'>${confirmValText} cannot be changed once the entity is created.</div>` : ""}
					<br/>
				</div>
				<div>Are these values correct?</div>`,
			showCloseButton: true,
			type: "info",
			showCancelButton: true,
			confirmButtonColor: "#8CC541",
			confirmButtonText: "Create Entity",
			focusCancel: true,
			width: "400px",
			reverseButtons: true
		});

		return !confirmation.dismiss;
	}

	function validate() {
		const validationStrings = [];

		if(ctrl.formEntity.Code === undefined) {
			validationStrings.push("Entity Number is required");
		}

		if(ctrl.entityType?.TimeZone?.IsRequired && !ctrl.formEntity.TimeZone?.Code) {
			validationStrings.push("Timezone is required");
		}

		if(
			(ctrl.entityType.IsNameDefaultedToCodeWhenNoContact !== true
				|| ctrl.formEntity.Contact !== undefined)
			&& (
				(isSupportedOrNullish(ctrl.entityType.Contact)
					&& ctrl.formEntity.Name === undefined
					&& ctrl.formEntity.Contact === undefined)
				|| (ctrl.entityType.Contact.IsRequired && ctrl.formEntity.Contact === undefined)
				|| (!ctrl.entityType.Contact.IsSupported && ctrl.formEntity.Name === undefined)
			)
		) {
			validationStrings.push("Entity Name is required");
		}

		if(ctrl.entityType.Classification?.IsRequired && ctrl.formEntity.Classification === undefined) {
			validationStrings.push("Classification is required");
		}

		if(ctrl.entityType.ExternalKey?.IsRequired
			&& (ctrl.formEntity.ExternalKey === undefined || ctrl.formEntity.ExternalKey.trim() === "")) {
			validationStrings.push("External key is required");
		}

		if(ctrl.entityType.CodePattern !== undefined) {
			const expression = new RegExp(ctrl.entityType.CodePattern);
			if(!expression.test(ctrl.formEntity.Code)) {
				validationStrings.push("Code is not a valid pattern");
			}
		}

		if(ctrl.entityType.NamePattern !== undefined) {
			const expression = new RegExp(ctrl.entityType.NamePattern);
			if(!expression.test(ctrl.formEntity.Name)) {
				validationStrings.push("Entity Name is not a valid pattern");
			}
		}

		if(ctrl.overviewSchema) {
			let validated = false;

			$scope.$broadcast("schemaFormValidate", {
				onValidated: (value, result) => {
					validated = true;
					ctrl.overviewModel = value;

					if(!result.valid) {
						validationStrings.push(...(result.messages.length ? result.messages : ["Please correct the invalid fields."]));
					}
				}
			});

			if(!validated) {
				validationStrings.push("This form cannot be validated.");
			}
		}

		if(validationStrings.length > 0) {
			const errorList = `<ul><li>${validationStrings.join("</li><li>")}</li></ul>`;

			swal.fire({
				title: "Correct Missing Information",
				html:
					`<p>Please correct the following errors</p>
					${errorList}`,
				showCloseButton: true,
				type: "warning"
			});

			return false;
		}

		return true;
	}
}

export default angular.module("vesl.components.entity-form-editor", [jsonSchemaForm])
	.component("entityFormEditor", {
		controller,
		template,
		bindings: {
			entity: "<",
			readonly: "<",
			entityType: "<",
			nameComponentOptions: "<",
			draft: "=?",
			onCreate: "&",
			onSave: "&",
			post: "="
		},
		require: { section: "^^viewModesSection" }
	})
	.name;
